import { useState } from "react";
import { Box, Button, Heading, Link, Stack, Text, useToast } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { RootState } from "redux/store";
import type { ApiKeySummary } from "api/ApiKeySettings";
import useApiRequest from "hooks/api/useApiHook";
import { RequestMethod, RequestName } from "hooks/api/apiHookTypes";
import { useApiCheckerSettings } from "hooks/api/use-api-checker-settings";
import { isUserApiKey, storeUserApiKey } from "utils/auth/apiKeyStore";
import { resolveSessionApiKey } from "utils/auth/recoverApiKey";
import { maskApiKey } from "utils/manipulation/maskApiKey";
import { FOCUS_RING } from "utils/chackra/controlStyles";
import { pendingBootstrapApiKey } from "utils/auth/sessionRestore";
import PageLoader from "components/UIComponents/PageLoader";
import ApiKeyPanel from "./ApiKeyPanel";
import ApiKeySummaryPanel from "./ApiKeySummaryPanel";
import ApiSettingsPanel from "./ApiSettingsPanel";
import ApiTestRequestPanel from "./ApiTestRequestPanel";
import Panel from "./Panel";

export const PROFILE_API_PATH = "/profile/api";
const API_DOCS_PATH = "/api-docs/getting-started";

/**
 * Cabinet → API (spec 2026-10-20-website-api-settings, Stage 6): the key, the
 * account behind it, the checker settings the bot also edits, and a free
 * test request.
 */
const ProfileApi = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const toast = useToast();
  const t = (id: string) => intl.formatMessage({ id });
  const storeKey = useSelector((state: RootState) => state.apiKey.apiKey);
  const apiKey = isUserApiKey(storeKey) ? storeKey : null;
  // The store gets the key one render after a hard load; until then a stored
  // key means "loading", and only its absence means "no key, create one".
  const keyLoading = apiKey === null && pendingBootstrapApiKey() !== null;
  const [revealed, setRevealed] = useState(false);
  const [creating, setCreating] = useState(false);

  const summaryRequest = useApiRequest({
    method: RequestMethod.GET,
    name: RequestName.API_KEY_SUMMARY,
    apiToken: apiKey ?? undefined,
  });
  const summaryBody = summaryRequest.data as { data?: ApiKeySummary } | null;
  const summary = summaryRequest.error ? null : summaryBody?.data ?? null;
  const settingsState = useApiCheckerSettings(apiKey);

  // Old accounts may have no key yet: the auth-service mints one for the
  // signed-in session (the same call the 401 recovery uses).
  const createKey = async () => {
    setCreating(true);
    try {
      storeUserApiKey(dispatch, await resolveSessionApiKey());
      setRevealed(true);
    } catch {
      toast({
        position: "top",
        status: "error",
        title: t("profileApi.key.createError"),
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  };

  const shownKey = apiKey ? (revealed ? apiKey : maskApiKey(apiKey)) : "";

  return (
    <Box maxW="4xl" mx="auto" px={{ base: 4, md: 0 }} pb={10} data-testid="profile-api">
      <Heading as="h1" fontSize="3xl" color="text.primary" mb={2}>
        {t("profileApi.title")}
      </Heading>
      <Text color="text.secondary" mb={6}>
        {t("profileApi.intro")}{" "}
        <Link
          as={RouterLink}
          to={API_DOCS_PATH}
          color="brand.solid"
          textDecoration="underline"
          _focusVisible={FOCUS_RING}
        >
          {t("profileApi.docsLink")}
        </Link>
      </Text>

      {apiKey ? (
        <Stack spacing={6}>
          <ApiKeyPanel
            apiKey={apiKey}
            shownKey={shownKey}
            revealed={revealed}
            onRevealedChange={setRevealed}
          />
          <ApiKeySummaryPanel
            summary={summary}
            loading={summaryRequest.loading}
            error={summaryRequest.error}
          />
          <ApiSettingsPanel state={settingsState} />
          <ApiTestRequestPanel
            apiKey={apiKey}
            shownKey={shownKey}
            requestsLeft={summary ? Number(summary.requests_left) || 0 : null}
          />
        </Stack>
      ) : keyLoading ? (
        <PageLoader label={t("profileApi.loading")} />
      ) : (
        <Panel id="api-key" title={t("profileApi.key.heading")}>
          <Text color="text.primary" mb={4}>
            {t("profileApi.key.none")}
          </Text>
          <Button
            colorScheme="blue"
            onClick={createKey}
            isLoading={creating}
            _focusVisible={FOCUS_RING}
          >
            {t("profileApi.key.create")}
          </Button>
        </Panel>
      )}
    </Box>
  );
};

export default ProfileApi;
